import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, Edit3, Loader2, Search, Sparkles, Link as LinkIcon, PencilLine, ExternalLink } from 'lucide-react';
import { getCurrentPosition } from '@/lib/geo';
import type { NewRestaurant } from '@/types';

type SearchMode = 'ai' | 'url' | 'name';

type SuggestItem = {
  id?: string;
  name: string;
  location: string;
  prefecture?: string;
  municipality?: string;
  lat?: number;
  lon?: number;
  cuisine?: string;
  priceRange?: string;
  image?: string;
  sourceUrl?: string;
  tiktokUrl?: string;
  distance?: number;
  confidence?: number;
  aiReason?: string;
  notes?: string;
};

type ApiError = {
  code?: string;
  error?: string;
};

type PlaceSearchProps = {
  onPickImmediate: (r: NewRestaurant) => void;
  onPickEdit: (r: Partial<NewRestaurant>) => void;
  onManual: () => void;
};

const MODE_META: Record<SearchMode, { label: string; icon: typeof Sparkles; placeholder: string; button: string; helper: string }> = {
  ai: {
    label: 'AIで探す',
    icon: Sparkles,
    placeholder: '例: 新宿で記念日に使える静かなフレンチ',
    button: '候補',
    helper: '希望文のほか、TikTokなどの投稿URLや投稿文を貼っても候補化します。',
  },
  url: {
    label: 'URLを貼る',
    icon: LinkIcon,
    placeholder: 'https://...',
    button: '読取',
    helper: '公式サイト、Googleマップ、食べログ、記事URL、TikTok投稿URLから読み取ります。投稿文も一緒に貼ると精度が上がります。',
  },
  name: {
    label: '店名で探す',
    icon: Search,
    placeholder: '例: ガスト 武蔵小金井駅前',
    button: '検索',
    helper: '店名や駅名が分かっているときの軽い検索です。',
  },
};

function confidenceLabel(value?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return `${Math.round(value * 100)}%`;
}

export default function PlaceSearch({ onPickImmediate, onPickEdit, onManual }: PlaceSearchProps) {
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<SearchMode>('ai');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<SuggestItem[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [summary, setSummary] = useState('');

  async function getCoords() {
    try {
      return await getCurrentPosition(5000);
    } catch {
      return {};
    }
  }

  async function run() {
    const input = q.trim();
    if (!input) return;
    setLoading(true);
    setErr(null);
    setItems([]);
    setSummary('');
    try {
      const pastedUrl = input.match(/https?:\/\/[^\s]+/i)?.[0] || '';
      if (mode === 'url' || pastedUrl) {
        const resp = await fetch('/api/extract', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: input }),
        });
        if (!resp.ok) throw await toSearchError(resp, 'URLの読取に失敗しました');
        const data = await resp.json();
        const sourceUrl = data.sourceUrl || pastedUrl || input;
        const tiktokUrl = data.tiktokUrl || (/tiktok\.com|vt\.tiktok\.com|vm\.tiktok\.com/i.test(sourceUrl) ? sourceUrl : '');
        setItems([{
          id: sourceUrl,
          name: data.name || '',
          cuisine: data.cuisine || '',
          location: data.location || '',
          prefecture: data.prefecture || '',
          municipality: data.municipality || '',
          notes: data.notes || '',
          aiReason: data.notes || '',
          image: data.image || '',
          priceRange: data.priceRange || '',
          sourceUrl,
          tiktokUrl,
          confidence: data.name ? 0.8 : 0.35,
        }].filter((item) => item.name));
        if (!data.name) setErr('店名を読み取れませんでした。TikTok本文に店名が出ていない場合は、投稿文を少し足してAIで探してください。');
        return;
      }

      const coords = await getCoords();
      const endpoint = mode === 'ai' ? '/api/ai-search' : '/api/search';
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: input, ...coords, limit: 10 }),
      });
      if (!resp.ok) throw await toSearchError(resp, '検索に失敗しました');
      const data = await resp.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setSummary(typeof data.summary === 'string' ? data.summary : '');
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : '検索に失敗しました');
    } finally {
      setLoading(false);
    }
  }

  async function toSearchError(resp: Response, fallback: string) {
    let detail: ApiError | null = null;
    try { detail = await resp.json() as ApiError; } catch { detail = null; }
    if (detail?.code === 'NO_OPENAI_KEY') return new Error('AI設定が未完了です。管理者設定を確認してください。');
    if (detail?.code === 'NO_TAVILY_KEY') return new Error('Web検索設定が未完了です。店名検索か手入力を使ってください。');
    if (detail?.code === 'OPENAI_RATE_LIMIT' || detail?.code === 'TAVILY_RATE_LIMIT') return new Error('検索が混み合っています。少し待ってから再検索してください。');
    if (detail?.code === 'OPENAI_AUTH' || detail?.code === 'TAVILY_AUTH') return new Error('検索エンジンの認証に失敗しました。管理者設定が必要です。');
    return new Error(detail?.error || fallback);
  }

  function asNewRestaurant(x: SuggestItem): NewRestaurant {
    return {
      name: x.name,
      cuisine: x.cuisine || '',
      location: x.location || '',
      prefecture: x.prefecture,
      municipality: x.municipality,
      priceRange: x.priceRange,
      image: x.image,
      sourceUrl: x.sourceUrl,
      tiktokUrl: x.tiktokUrl,
      notes: x.notes || x.aiReason,
      isWishlist: true,
      lat: x.lat,
      lon: x.lon,
    };
  }

  const meta = MODE_META[mode];

  return (
    <Card className="border-0 shadow-card p-3 sm:p-4">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-card-foreground">Step 1 追加方法を選ぶ</div>
          <div className="text-xs text-muted-foreground">探す、貼る、店名検索のどれでも始められます。</div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onManual}>
          <PencilLine className="mr-1 h-4 w-4" />手入力
        </Button>
      </div>

      <div className="mb-3 grid grid-cols-3 rounded-md border bg-background p-1">
        {(Object.keys(MODE_META) as SearchMode[]).map((key) => {
          const Icon = MODE_META[key].icon;
          return (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={mode === key ? 'secondary' : 'ghost'}
              onClick={() => { setMode(key); setItems([]); setErr(null); setSummary(''); }}
              className="h-9 px-2"
            >
              <Icon className="mr-1 h-4 w-4" />
              <span className="truncate">{MODE_META[key].label}</span>
            </Button>
          );
        })}
      </div>

      <div className="mb-1 flex flex-col gap-2 sm:flex-row">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={meta.placeholder}
          className="border-input"
          onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
        />
        <Button onClick={run} disabled={loading || !q.trim()} className="sm:w-24">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : meta.button}
        </Button>
      </div>
      <div className="mb-3 text-xs text-muted-foreground">{meta.helper}</div>

      {err && <div className="mb-2 text-xs text-red-600">{err}</div>}
      {summary && <div className="mb-2 text-xs text-muted-foreground">{summary}</div>}

      {items.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-card-foreground">Step 2 候補を確認</div>
          {items.map((it, idx) => (
            <div key={(it.id || it.sourceUrl || '') + idx} className="grid gap-3 rounded-md border bg-card p-2 sm:grid-cols-[88px_1fr_auto]">
              <div className="h-20 overflow-hidden rounded bg-muted sm:h-[88px]">
                {it.image ? (
                  <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center text-muted-foreground">
                    <MapPin className="h-6 w-6" />
                  </div>
                )}
              </div>
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-medium text-card-foreground">{it.name}</div>
                  {confidenceLabel(it.confidence) && <Badge variant="outline">信頼度 {confidenceLabel(it.confidence)}</Badge>}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {it.cuisine && <Badge variant="secondary">{it.cuisine}</Badge>}
                  {it.priceRange && <Badge variant="outline">{it.priceRange}</Badge>}
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{it.location || it.prefecture || '場所未取得'}</span>
                  </span>
                  {typeof it.distance === 'number' && <span>約{Math.round(it.distance)}m</span>}
                </div>
                {(it.notes || it.aiReason) && <div className="line-clamp-2 text-xs text-muted-foreground">{it.notes || it.aiReason}</div>}
                {it.sourceUrl && (
                  <a href={it.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 text-xs text-primary underline">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">根拠URL</span>
                  </a>
                )}
                {it.tiktokUrl && it.tiktokUrl !== it.sourceUrl && (
                  <a href={it.tiktokUrl} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex max-w-full items-center gap-1 text-xs text-primary underline">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">TikTok</span>
                  </a>
                )}
              </div>
              <div className="flex gap-2 sm:flex-col sm:justify-center">
                <Button size="sm" variant="secondary" onClick={() => onPickImmediate(asNewRestaurant(it))}>
                  <Plus className="mr-1 h-4 w-4" />追加
                </Button>
                <Button size="sm" variant="outline" onClick={() => onPickEdit(asNewRestaurant(it))}>
                  <Edit3 className="mr-1 h-4 w-4" />編集
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
